import { useRef, useEffect } from 'react';
import { useContent } from '../context/ContentContext';

/**
 * Renders text that is editable in dev (npm run dev) and read-only in production.
 * - path: dot-notation key in content-config (e.g. "hero.tagline")
 * - tag: HTML element type (default "span")
 * - fallback: used when path is missing
 * - className: optional
 */
export default function EditableText({ path, tag: Tag = 'span', fallback = '', className = '' }) {
  const { getText, setText, configLoaded, isEditMode } = useContent();
  const ref = useRef(null);

  const value = getText(path) ?? fallback;

  // Sync DOM when path/config loads, or when edit mode turns on (so content appears; we don't use value in deps to avoid overwriting while typing)
  useEffect(() => {
    if (!ref.current || !configLoaded) return;
    ref.current.textContent = value;
  }, [path, configLoaded, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps -- value intentionally omitted to avoid overwriting while typing

  if (!isEditMode) {
    return <Tag className={className}>{value}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      tabIndex={-1}
      className={`${className} outline-none focus:ring-1 focus:ring-[#9a83ad] rounded px-0.5 -mx-0.5 cursor-text`}
      style={{ color: 'inherit', background: 'transparent' }}
      onInput={(e) => {
        const newVal = e.currentTarget.innerText ?? '';
        setText(path, newVal);
      }}
    />
  );
}
